import { Modal } from "@@/modals/modal";
import { ModalHeader } from "@@/modals/modal-header";
import { useModal } from "@@/modals/use-modal";
import { Conditional } from "@@/shared/conditional";
import { HorizontalDivider, VerticalDivider } from "@@/shared/dividers";
import { FlexColumn, FlexRow } from "@@/shared/flex-containers";
import { _FormFieldDescription } from "@@/shared/form/_form-field-description";
import type { FormId } from "@@/shared/form/form-and-field-id";
import { useFormId } from "@@/shared/form/form-id.context";

import { FormErrorMessages } from "@@/shared/form/form2-error-messages";
import { Icon } from "@@/shared/icons/icon";
import { usePageBreakpoint } from "@@/shared/responsiveness/use-page-breakpoint";
import { FieldTitle } from "@@/shared/text/field-title";
import { ModalTitle } from "@@/shared/text/modal-title";
import { useDateTimeFormatter } from "@@/shared/use-date-time-formatter";
import { useTheme } from "@emotion/react";
import { faCalendar } from "@fortawesome/pro-regular-svg-icons";
import type {
    DeepKeysOf,
    IsoAndUnixTimestamp,
    Translatable,
    TranslatableValueFactoryParams,
} from "@towni/common";
import {
    asDate,
    dateRangeUtils,
    generateId,
    getNameOfDay,
    isoAndUnixFactory,
    isSameDayInTimezone,
    translation,
} from "@towni/common";
import { useCallback, useEffect, useState } from "react";
import type { Matcher, PropsBase, PropsSingle } from "react-day-picker";
import "react-day-picker/dist/style.css";
import type { Except } from "type-fest";
import type { ZodSchema } from "zod";
import { Calendar } from "../components/calendar";
import { Column } from "../components/column";
import { Row } from "../components/row";
import type { FloatingBoxActions } from "../floating-box";
import { FloatingBox } from "../floating-box";
import { Span } from "../text/span";
import { cn } from "../utils/cn";
import type { Form3ErrorMessage } from "./form3.error-message";
import { useForm3Field } from "./form3.use-form-field";
import { useDayPickerDefaults } from "./shared/use-day-picker-defaults";

type ValueType = IsoAndUnixTimestamp;

type Props<FieldId> = {
    readonly formId?: FormId;
    readonly fieldId: FieldId;
    readonly className?: string;
    readonly triggerClassName?: string;
    readonly fieldSchema?: ZodSchema;

    readonly disabled?: boolean;
    readonly label?: Translatable | TranslatableValueFactoryParams;
    readonly labelDescription?: Translatable | TranslatableValueFactoryParams;
    readonly description?: Translatable | TranslatableValueFactoryParams;
    readonly hideDescriptionAfterInput?: boolean;
    readonly compact?: boolean;

    readonly datePickerProps?: Except<
        PropsBase & PropsSingle,
        "selected" | "onSelect" | "mode" | "disabled"
    >;
    readonly hideYearAndMonthPicker?: boolean;
};

const createForm3DatePickerComponent = <
    FormSchema extends ZodSchema,
    FormData extends Record<string, unknown>,
>() => {
    const Form3DatePicker = <
        FieldId extends DeepKeysOf<FormData> = DeepKeysOf<FormData>,
    >(
        props: Props<FieldId>,
    ) => {
        const theme = useTheme();
        const dayPickerDefaults = useDayPickerDefaults();
        const [errors, setErrors] = useState<Form3ErrorMessage[]>([]);
        const hasErrors = errors.length > 0;
        const [{ show, hide }, modalId] = useModal("day_picker_modal__");
        const isSmallScreen = usePageBreakpoint({ when: "SMALL_SCREEN" });
        const isCompact = props.compact ?? true;
        const { formatDateMedium } = useDateTimeFormatter();
        const formIdFromContext = useFormId({ doNotThrow: true });
        const formId = props.formId || formIdFromContext;
        const field = useForm3Field<FormSchema, FormData, FieldId, ValueType>({
            fieldId: props.fieldId,
            formId: props.formId,
        });

        const [_initialValue] = useState(field.value);
        const [key, setKey] = useState(generateId);
        const [defaultMonth, _setDefaultMonth] = useState<Date | undefined>(
            asDate(field.value),
        );
        const setDefaultMonth = useCallback((date: Date | undefined) => {
            _setDefaultMonth(date);
            setKey(generateId());
        }, []);

        const [pickerIsOpen, setPickerIsOpen] = useState(false);

        // Custom modifiers object
        const selectedAndTodayModifier: Matcher = (day): boolean => {
            return (
                asDate(field.value)?.toDateString() === day.toDateString() &&
                day.toDateString() === new Date().toDateString()
            );
        };

        const dayPicker = (
            params: FloatingBoxActions & {
                containerClassName?: string;
                className?: string;
            },
        ) => (
            <Calendar
                key={`${defaultMonth?.toISOString()}_${key}`}
                {...(props.datePickerProps ?? {})}
                mode="single"
                captionLayout={
                    props.hideYearAndMonthPicker ? "label" : "dropdown"
                }
                defaultMonth={defaultMonth}
                disabled={props.disabled || field.form?.mutation?.isPending}
                selected={asDate(field.value)}
                onDayBlur={(_date, _modifiers, _focusEvent) => {
                    field.setTouched(true);
                    setErrors(field.validate());
                }}
                modifiers={{
                    selectedAndToday: selectedAndTodayModifier,
                }}
                className={params.className}
                containerClassName={params.containerClassName}
                css={{
                    ...dayPickerDefaults.css,
                    padding: 10,
                }}
                // styles={dayPickerDefaults.styles}
                // modifiersStyles={{
                //     ...dayPickerDefaults.modifiersStyles,
                //     selectedAndToday: {
                //         backgroundColor: theme.colors.primary.asString,
                //         borderRadius: 3000,
                //         borderColor:
                //             theme.colors.primary.border.withAlpha(0.5).asString,
                //         color: theme.colors.primary.text.asString,
                //         textDecoration: "underline",
                //         fontWeight: 400,
                //     },
                // }}
                locale={dayPickerDefaults.locale}
                onSelect={date => {
                    const newValue =
                        date instanceof Date
                            ? isoAndUnixFactory(date)
                            : undefined;
                    field.setValue(newValue);
                    field.setDirty(true);
                    field.setTouched(true);
                    if (isCompact) {
                        params.close();
                    }
                }}
            />
        );

        const [label, labelDescription] = (() => {
            if (!field.value)
                return [
                    translation({
                        sv: "Välj datum",
                        en: "Pick a date",
                    }),
                    null,
                ];

            const _label = formatDateMedium(field.value, {
                includeYearIfNotCurrent: true,
            });
            if (
                isSameDayInTimezone(field.value, dateRangeUtils.today().start)
            ) {
                return [
                    _label,
                    {
                        sv: "Idag",
                        en: "Today",
                    },
                ];
            }
            if (
                isSameDayInTimezone(
                    field.value,
                    dateRangeUtils.tomorrow().start,
                )
            ) {
                return [
                    _label,
                    {
                        sv: "Imorgon",
                        en: "Tomorrow",
                    },
                ];
            }
            if (
                isSameDayInTimezone(
                    field.value,
                    dateRangeUtils.yesterday().start,
                )
            ) {
                return [
                    _label,
                    {
                        sv: "Igår",
                        en: "Yesterday",
                    },
                ];
            }

            return [_label, getNameOfDay(field.value)];
        })();

        const trigger = (actions: FloatingBoxActions) => (
            <Row
                crossAxis="center"
                onClick={actions.toggle}
                className={cn(
                    "bg-textInput-surface border-textInput-outline cursor-pointer rounded-sm border",
                    props.triggerClassName,
                )}>
                <Row crossAxis="center" className="flex-1 space-x-2 px-4 py-3">
                    <Span
                        text={label}
                        className={cn("!text-nowrap", {
                            "text-textInput-copy-6": !field.value,
                        })}
                    />
                    {labelDescription ? (
                        <Span
                            text={labelDescription}
                            className="text-textInput-copy-6"
                        />
                    ) : null}
                </Row>
                <HorizontalDivider />
                <Icon
                    icon={faCalendar}
                    fixedWidth
                    className={cn("pr-2.5", {
                        "opacity-50": props.disabled,
                    })}
                />
            </Row>
        );

        useEffect(() => {
            if (!isSmallScreen) return;
            if (pickerIsOpen) show();
            else hide();
        }, [isSmallScreen, pickerIsOpen, show, hide]);

        const picker = isSmallScreen ? (
            <>
                {trigger({
                    close: () => {
                        setPickerIsOpen(false);
                    },
                    open: () => {
                        setPickerIsOpen(true);
                    },
                    toggle: () => {
                        setPickerIsOpen(prev => !prev);
                    },
                })}
                <Modal
                    modalId={modalId}
                    onHide={() => {
                        setDefaultMonth(asDate(field.value));
                    }}
                    header={
                        <ModalHeader
                            modalId={modalId}
                            bottomBorder
                            title={
                                <ModalTitle
                                    text={translation({
                                        sv: "Välj datum",
                                        en: "Pick a date",
                                    })}
                                />
                            }
                        />
                    }>
                    <Column
                        mainAxis="center"
                        crossAxis="center"
                        className="w-full">
                        {dayPicker({
                            close: () => {
                                setPickerIsOpen(false);
                            },
                            open: () => {
                                setPickerIsOpen(true);
                            },
                            toggle: () => {
                                setPickerIsOpen(prev => !prev);
                            },
                            containerClassName: "border-0",
                        })}
                    </Column>
                </Modal>
            </>
        ) : (
            <FloatingBox
                content={dayPicker}
                trigger={trigger}
                onClose={() => {
                    setDefaultMonth(asDate(field.value));
                }}
            />
        );

        return (
            <FlexColumn
                key={`${formId}_${props.fieldId}`}
                tag={`form-field_${props.fieldId}`}
                className={props.className}
                crossAxis="stretch">
                <Conditional when={!!props.label}>
                    <FlexRow
                        crossAxis="center"
                        mainAxis="space-between"
                        css={{
                            flexShrink: 0,
                        }}>
                        <FieldTitle
                            htmlFor={props.fieldId}
                            padding={{ left: 2 }}
                            text={props.label ?? ""} // already checked with conditional above
                            required={field.isRequired}
                        />
                        <Conditional when={!!props.labelDescription}>
                            <HorizontalDivider XXS />
                            <FieldTitle
                                padding={{ left: 2 }}
                                text={props.labelDescription ?? ""} // already checked with conditional above
                                weight="400"
                                size="S"
                                css={{
                                    opacity: 0.5,
                                }}
                            />
                        </Conditional>
                    </FlexRow>
                    <VerticalDivider XS />
                </Conditional>
                {!isCompact ? (
                    <div
                        css={{
                            width: "100%",
                            backgroundColor: "white",
                            borderRadius: 10,
                            borderStyle: "solid",
                            borderWidth: 1,
                            borderColor: theme.colors.textInput.border.asString,
                        }}>
                        {dayPicker({
                            close: () => {
                                setPickerIsOpen(false);
                            },
                            open: () => {
                                setPickerIsOpen(true);
                            },
                            toggle: () => {
                                setPickerIsOpen(prev => !prev);
                            },
                        })}
                    </div>
                ) : (
                    <>{picker}</>
                )}
                <_FormFieldDescription
                    hasErrors={hasErrors}
                    isDirty={field.dirty}
                    description={props.description}
                    hideDescriptionAfterInput={
                        !!props.hideDescriptionAfterInput
                    }
                    css={{
                        marginTop: 5,
                    }}
                />
                <FormErrorMessages errors={errors} />
            </FlexColumn>
        );
    };
    return Form3DatePicker;
};
export { createForm3DatePickerComponent };
